import { hyperEVM } from '@/config/chain/constants'
import { CARD_API_ENDPOINT } from '@/config/consts'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { useVaultClassifier } from '../contexts/VaultClassifierContext'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  type BorrowTokenOption,
  type EulerVaultInfo,
  type MarketResolver,
  STABLECOIN_DECIMALS,
  STABLECOIN_LABELS,
} from '../constants'
import { createSmartGet } from './useAuthenticatedMutation'

interface SolverInfoResponseItem {
  id: string
  mode?: string
  config?: unknown
}

interface SolverInfoResponse {
  data?: Record<string, SolverInfoResponseItem[]>
}

type JsonObj = Record<string, unknown>

function isObj(value: unknown): value is JsonObj {
  return typeof value === 'object' && value !== null
}

function asAddress(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('0x')) return null
  return value
}

function tokenDecimals(tokenAddress: string): number {
  const entry = Object.entries(STABLECOIN_DECIMALS).find(
    ([address]) => address.toLowerCase() === tokenAddress.toLowerCase(),
  )
  return entry?.[1] ?? 6
}

function tokenSymbol(tokenAddress: string): string {
  return STABLECOIN_LABELS[tokenAddress.toLowerCase()] ?? `${tokenAddress.slice(0, 6)}...${tokenAddress.slice(-4)}`
}

function parseHypurrFiBorrowTokens(config: unknown): BorrowTokenOption[] {
  if (!isObj(config) || !isObj(config.borrowTokenConfigs)) return []

  return Object.keys(config.borrowTokenConfigs)
    .map((address) => asAddress(address))
    .filter((address): address is string => !!address)
    .map((address) => ({
      address,
      symbol: tokenSymbol(address),
      decimals: tokenDecimals(address),
    }))
}

function parseBorrowDebtTokenMap(config: unknown): Record<string, string> {
  if (!isObj(config) || !isObj(config.borrowTokenConfigs)) return {}

  const entries = Object.entries(config.borrowTokenConfigs)
    .map(([borrowToken, entry]) => {
      if (!isObj(entry)) return null
      const borrow = asAddress(borrowToken)
      const debt = asAddress(entry.debtTokenAddress)
      if (!borrow || !debt) return null
      return [borrow.toLowerCase(), debt] as const
    })
    .filter((entry): entry is readonly [string, string] => !!entry)

  return Object.fromEntries(entries)
}

function parseWithdrawATokenMap(config: unknown): Record<string, string> {
  if (!isObj(config) || !isObj(config.aTokenConfigs)) return {}

  const entries = Object.values(config.aTokenConfigs)
    .map((entry) => {
      if (!isObj(entry)) return null
      const base = asAddress(entry.baseTokenAddress)
      const aToken = asAddress(entry.aTokenAddress)
      if (!base || !aToken) return null
      return [base.toLowerCase(), aToken] as const
    })
    .filter((entry): entry is readonly [string, string] => !!entry)

  return Object.fromEntries(entries)
}

function parseHypurrFiWithdrawTokens(config: unknown): BorrowTokenOption[] {
  if (!isObj(config) || !isObj(config.aTokenConfigs)) return []

  const addresses = Object.values(config.aTokenConfigs)
    .map((entry) => (isObj(entry) ? asAddress(entry.baseTokenAddress) : null))
    .filter((address): address is string => !!address)

  const unique = [...new Set(addresses.map((address) => address.toLowerCase()))]
  return unique.map((lowerAddress) => ({
    address: addresses.find((address) => address.toLowerCase() === lowerAddress) ?? lowerAddress,
    symbol: tokenSymbol(lowerAddress),
    decimals: tokenDecimals(lowerAddress),
  }))
}

function parseEulerVaults(config: unknown, resolveMarket?: MarketResolver): EulerVaultInfo[] {
  if (!isObj(config) || !isObj(config.borrowVaultToConfigs)) return []

  return Object.entries(config.borrowVaultToConfigs)
    .map(([vaultAddress, vaultConfig]) => {
      if (!isObj(vaultConfig)) return null
      const borrowToken = asAddress(vaultConfig.borrowTokenAddress)
      const vault = asAddress(vaultAddress)
      if (!borrowToken || !vault) return null

      const symbol = tokenSymbol(borrowToken)
      const market = resolveMarket ? resolveMarket(vault as `0x${string}`) : getVaultMarket(vault as `0x${string}`)
      const clusterLabel =
        market?.key === 'hypurrfi-prime' ? 'Prime' : market?.key === 'hypurrfi-yield' ? 'Yield' : 'Euler'

      return {
        address: vault,
        borrowToken,
        borrowTokenSymbol: symbol,
        label: `${clusterLabel} ${symbol}`,
      } satisfies EulerVaultInfo
    })
    .filter((value): value is EulerVaultInfo => !!value)
}

function parseCreditReceiverAddress(config: unknown): string | undefined {
  if (!isObj(config) || !isObj(config.borrowTokenConfigs)) return undefined

  for (const entry of Object.values(config.borrowTokenConfigs)) {
    if (!isObj(entry)) continue
    const value = asAddress(entry.creditReceiverAddress)
    if (value) return value
  }

  return undefined
}

function parseCreditReceiverModule(config: unknown): string | undefined {
  if (!isObj(config)) return undefined

  const direct = asAddress(config.creditReceiverModule)
  if (direct) return direct

  if (!isObj(config.aTokenConfigs)) return undefined
  for (const entry of Object.values(config.aTokenConfigs)) {
    if (!isObj(entry)) continue
    const value = asAddress(entry.creditReceiverModule)
    if (value) return value
  }

  return undefined
}

function hasBorrowTokenConfigs(config: unknown): boolean {
  return isObj(config) && isObj(config.borrowTokenConfigs)
}

function hasATokenConfigs(config: unknown): boolean {
  return isObj(config) && isObj(config.aTokenConfigs)
}

function hasBorrowVaultToConfigs(config: unknown): boolean {
  return isObj(config) && isObj(config.borrowVaultToConfigs)
}

function findSolverInfoByIdAndModeWithConfig(
  chainItems: SolverInfoResponseItem[],
  id: string,
  mode: 'borrow' | 'spend',
  matchesConfig: (config: unknown) => boolean,
): SolverInfoResponseItem | undefined {
  return chainItems.find((item) => item.id === id && item.mode === mode && matchesConfig(item.config))
}

function findSolverInfoByIdWithoutMode(
  chainItems: SolverInfoResponseItem[],
  id: string,
  matchesConfig: (config: unknown) => boolean,
): SolverInfoResponseItem | undefined {
  return chainItems.find((item) => item.id === id && !item.mode && matchesConfig(item.config))
}

function findSolverInfoByIdWithConfig(
  chainItems: SolverInfoResponseItem[],
  id: string,
  matchesConfig: (config: unknown) => boolean,
): SolverInfoResponseItem | undefined {
  return chainItems.find((item) => item.id === id && matchesConfig(item.config))
}

export function resolveSolverInfoItems(chainItems: SolverInfoResponseItem[]) {
  const eulerBorrowInfo =
    findSolverInfoByIdWithoutMode(chainItems, 'euler', hasBorrowVaultToConfigs) ??
    findSolverInfoByIdAndModeWithConfig(chainItems, 'euler', 'borrow', hasBorrowVaultToConfigs) ??
    findSolverInfoByIdWithConfig(chainItems, 'euler', hasBorrowVaultToConfigs)
  const eulerSpendInfo =
    findSolverInfoByIdWithoutMode(chainItems, 'euler-withdraw', hasBorrowVaultToConfigs) ??
    findSolverInfoByIdWithConfig(chainItems, 'euler-withdraw', hasBorrowVaultToConfigs) ??
    findSolverInfoByIdAndModeWithConfig(chainItems, 'euler', 'spend', hasBorrowVaultToConfigs)
  const hypurrFiBorrowInfo =
    findSolverInfoByIdWithoutMode(chainItems, 'hypurrFi', hasBorrowTokenConfigs) ??
    findSolverInfoByIdAndModeWithConfig(chainItems, 'hypurrFi', 'borrow', hasBorrowTokenConfigs) ??
    findSolverInfoByIdWithConfig(chainItems, 'hypurrFi', hasBorrowTokenConfigs)
  const hypurrFiSpendInfo =
    findSolverInfoByIdWithoutMode(chainItems, 'hypurrFi-withdraw', hasATokenConfigs) ??
    findSolverInfoByIdWithConfig(chainItems, 'hypurrFi-withdraw', hasATokenConfigs) ??
    findSolverInfoByIdAndModeWithConfig(chainItems, 'hypurrFi', 'spend', hasATokenConfigs)

  return {
    eulerBorrowInfo,
    eulerSpendInfo,
    hypurrFiBorrowInfo,
    hypurrFiSpendInfo,
  }
}

export function useSolverOptions() {
  const url = `${CARD_API_ENDPOINT}/v1/vendor/cards/info`
  const smartGet = createSmartGet(url)

  const { data, isLoading, error } = useQuery({
    queryKey: ['card-solver-options', hyperEVM.id],
    queryFn: () => smartGet() as Promise<SolverInfoResponse>,
    staleTime: 3_600_000,
    gcTime: 7_200_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const { resolveMarket } = useVaultClassifier()

  const chainItems = data?.data?.[String(hyperEVM.id)] ?? []
  const { eulerBorrowInfo, eulerSpendInfo, hypurrFiBorrowInfo, hypurrFiSpendInfo } = resolveSolverInfoItems(chainItems)

  const parsedEulerVaults = useMemo(
    () => parseEulerVaults(eulerBorrowInfo?.config, resolveMarket),
    [eulerBorrowInfo?.config, resolveMarket],
  )
  const parsedEulerWithdrawVaults = useMemo(
    () => parseEulerVaults(eulerSpendInfo?.config, resolveMarket),
    [eulerSpendInfo?.config, resolveMarket],
  )

  const hasEulerSpendMode = parsedEulerWithdrawVaults.length > 0
  const parsedHypurrFiBorrowTokens = parseHypurrFiBorrowTokens(hypurrFiBorrowInfo?.config)
  const parsedHypurrFiWithdrawTokens = parseHypurrFiWithdrawTokens(hypurrFiSpendInfo?.config)
  const hypurrFiDebtTokenByBorrowToken = parseBorrowDebtTokenMap(hypurrFiBorrowInfo?.config)
  const hypurrFiATokenByBaseToken = parseWithdrawATokenMap(hypurrFiSpendInfo?.config)
  const pooledBorrowReceiverAddress = parseCreditReceiverAddress(hypurrFiBorrowInfo?.config)
  const pooledSpendReceiverModule = parseCreditReceiverModule(hypurrFiSpendInfo?.config)

  const eulerBorrowVaults = parsedEulerVaults
  const hypurrFiBorrowTokens = parsedHypurrFiBorrowTokens
  const hypurrFiWithdrawTokens = parsedHypurrFiWithdrawTokens

  const allPooledTokensMap = new Map<string, BorrowTokenOption>()
  for (const token of [...hypurrFiBorrowTokens, ...hypurrFiWithdrawTokens]) {
    allPooledTokensMap.set(token.address.toLowerCase(), token)
  }

  const primeEulerVaults = useMemo(
    () => eulerBorrowVaults.filter((vault) => resolveMarket(vault.address as `0x${string}`)?.key === 'hypurrfi-prime'),
    [eulerBorrowVaults, resolveMarket],
  )
  const yieldEulerVaults = useMemo(
    () => eulerBorrowVaults.filter((vault) => resolveMarket(vault.address as `0x${string}`)?.key === 'hypurrfi-yield'),
    [eulerBorrowVaults, resolveMarket],
  )
  const primeEulerSpendVaults = useMemo(
    () =>
      parsedEulerWithdrawVaults.filter((vault) => resolveMarket(vault.address as `0x${string}`)?.key === 'hypurrfi-prime'),
    [parsedEulerWithdrawVaults, resolveMarket],
  )
  const yieldEulerSpendVaults = useMemo(
    () =>
      parsedEulerWithdrawVaults.filter((vault) => resolveMarket(vault.address as `0x${string}`)?.key === 'hypurrfi-yield'),
    [parsedEulerWithdrawVaults, resolveMarket],
  )

  return {
    isLoading,
    error,
    eulerBorrowVaults,
    eulerWithdrawVaults: parsedEulerWithdrawVaults,
    hasEulerSpendMode,
    yieldEulerVaults,
    primeEulerVaults,
    yieldEulerSpendVaults,
    primeEulerSpendVaults,
    hypurrFiBorrowTokens,
    hypurrFiWithdrawTokens,
    hypurrFiSupportedTokens: [...allPooledTokensMap.values()],
    hypurrFiDebtTokenByBorrowToken,
    hypurrFiATokenByBaseToken,
    pooledBorrowReceiverAddress,
    pooledSpendReceiverModule,
  }
}
